import { LabelAssignment, PreSoldMembership } from '@provision/entity-types';
import {
  ApolloClient,
  gql,
  QueryHookOptions,
  Reference,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import {
  BRAND_FIELDS,
  GET_CONVERSATION_QUERY,
  mapAddress,
  updateCachedConversation,
} from 'src/graphql';
import { useEntityAssignments } from 'src/graphql/entities/label-assignment';
import {
  ConditionInput,
  DeepPartial,
  EntityNames,
  NullableDeepPartial,
  QueryVariables,
  Records,
  SearchInput,
  SortingInput,
} from 'src/graphql/types';
import { generateFieldModifiersMap, useLoadMore } from 'src/graphql/utils';
import useConversationViewsStore from 'src/store/conversation-views';
import type { AddressRaw, ClientRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { sortAlphabetically } from 'src/utils/sorting';
import { ADDRESS_DATA_RESPONSE_FIELDS, ADDRESS_FIELDS } from './address.fragments';

const ADDRESS_DUPLICATE_SITE_MESSAGE =
  'Unable to create the site. A site with this address already exists.';

const ADDRESS_DUPLICATE_ERROR_MARKERS = [
  'duplicate key value violates unique constraint',
  'idx_unique_addresses',
];

const isAddressDuplicateError = (error: {
  message?: string;
  graphQLErrors?: readonly { message?: string }[];
}) => {
  const hasMarker = (message = '') =>
    ADDRESS_DUPLICATE_ERROR_MARKERS.some((marker) => message.includes(marker));

  if (hasMarker(error.message)) {
    return true;
  }

  return (error.graphQLErrors ?? []).some((graphQlError) => hasMarker(graphQlError.message));
};

export const GET_ADDRESSES_QUERY = gql`
  query GetAddress(
    $params: QueryParamsWithSearch
    $skipTotalRecords: Boolean = false
    $hasCustomAttributes: Boolean = false
  ) @entity(name: "Address") {
    getAddress(queryParams: $params) {
      ...AddressDataResponseFields
    }
  }
  ${ADDRESS_DATA_RESPONSE_FIELDS}
`;

const UPDATE_ADDRESS_MUTATION = gql`
  mutation UpdateAddress($conditions: [ConditionInput], $data: AddressDataInput) {
    updateAddress(conditions: $conditions, data: $data) {
      ...AddressFields
    }
  }
  ${ADDRESS_FIELDS}
`;

export const DELETE_CONVERSATION_TO_ADDRESS_MUTATION = gql`
  mutation DeleteConversationToAddress($conditions: [ConditionInput]) {
    deleteConversationToAddress(conditions: $conditions) {
      conversationId
      partnerId
      addressId
      createdAt
    }
  }
`;

export const CREATE_CONVERSATION_TO_ADDRESS_MUTATION = gql`
  mutation CreateConversationToAddress($data: ConversationToAddressDataInput) {
    createConversationToAddress(data: $data) {
      conversationId
      partnerId
      addressId
    }
  }
`;

export type GetAddressesQuery = {
  getAddress: Records<AddressRaw & { customAttributes?: string | null }>;
};

type ClientToAddress = {
  clientId: string;
  addressId: string;
  partnerId?: number;
  createdAt?: string;
};

export const CREATE_CLIENT_TO_ADDRESS_MUTATION = gql`
  mutation CreateClientToAddress($data: ClientToAddressDataInput) {
    createClientToAddress(data: $data) {
      clientId
      addressId
      createdAt
      partnerId
    }
  }
`;

export const ASSOCIATE_CLIENTS_WITH_ADDRESS_MUTATION = gql`
  mutation AssociateClientsWithAddress($data: AssociateClientsWithAddressInput!) {
    associateClientsWithAddress(data: $data) {
      success
    }
  }
`;

export const DELETE_CLIENT_TO_ADDRESS_MUTATION = gql`
  mutation DeleteClientToAddress($conditions: [ConditionInput]) {
    deleteClientToAddress(conditions: $conditions) {
      clientId
      addressId
    }
  }
`;

export const CREATE_ADDRESS_MUTATION = gql`
  mutation CreateAddress($data: AddressDataInput) {
    createAddress(data: $data) {
      ...AddressFields
      brand {
        ...BrandFields
      }
    }
  }
  ${ADDRESS_FIELDS}
  ${BRAND_FIELDS}
`;

export const MERGE_ADDRESS_MUTATION = gql`
  mutation MergeAddress($params: MergeAddressInput!) {
    mergeAddress(params: $params) {
      error
      success
    }
  }
`;

interface CreateAddressValues {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  businessName: string;
  ownerId: string;
  brandId: string;
  orgNotes: string;
  partnerId: number;
  primaryClientId?: string;
}

export type Address = Pick<
  AddressRaw,
  | 'id'
  | 'addressId'
  | 'partnerId'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'zip'
  | 'clientActivatedMembership'
  | 'platformSlaId'
  | 'clientSelectedMembership'
  | 'businessName'
  | 'brandId'
  | 'orgNotes'
  | 'ownerId'
  | 'brand'
  | 'rsmNotes'
  | 'isMonitored'
  | 'cppExpDate'
  | 'creditCardProvided'
  | 'chargifySubId'
  | 'primaryClientId'
  | 'tosAccepted'
  | 'membershipSalesNotes'
  | 'primaryProjectId'
  | 'projects'
  | 'partner'
  | 'addressSubscriptions'
  | 'deletedAt'
> & {
  fullAddress?: string;
  ownerFullName?: string;
  name?: string;
  address?: string;
  isDeleted?: boolean;
  preSoldMemberships?: Partial<PreSoldMembership>[] | null;
  labelAssignments?: LabelAssignment[] | null;
};

export type AddressMap = Record<string, Address[]>;

export const useAddresses = (
  params: {
    clientIds?: string | string[];
    filters?: ConditionInput[];
    sort?: SortingInput | SortingInput[];
    desc?: boolean;
    limit?: number;
    searchParams?: SearchInput;
    dynamicAttributePaths?: string[];
    includeDeleted?: boolean;
  },
  options?: QueryHookOptions<GetAddressesQuery>,
) => {
  const queryOptions = useMemo(() => {
    const {
      clientIds,
      filters,
      sort,
      desc = false,
      limit,
      searchParams,
      dynamicAttributePaths,
      includeDeleted,
    } = params;
    const readyFilters = [...(filters ?? [])];

    if (clientIds) {
      readyFilters.push({
        attribute: 'clients/clientId',
        operator: 'in',
        value: Array.isArray(clientIds) ? clientIds.sort().join(',') : clientIds,
      });
    }

    let sorting = sort;

    if (!sorting && !searchParams) {
      sorting = [
        { attribute: 'city', desc },
        { attribute: 'address1', desc },
      ];
    }

    if (sorting) {
      const sortingList = Array.isArray(sorting) ? sorting : [sorting];
      if (!sortingList.some((s) => s.attribute === 'addressId')) {
        sorting = [...sortingList, { attribute: 'addressId' }];
      }
    }

    return {
      ...options,
      notifyOnNetworkStatusChange: true,
      skip: options?.skip || (Array.isArray(clientIds) && !clientIds.length),
      variables: {
        skipTotalRecords: !!searchParams,
        hasCustomAttributes: Boolean(dynamicAttributePaths?.length),
        params: {
          searchParams,
          sorting,
          filters: readyFilters,
          offset: 0,
          limit,
          includeDeleted,
        },
      },
    };
  }, [options, params]);

  const useQueryResult = useQuery<GetAddressesQuery, QueryVariables>(
    GET_ADDRESSES_QUERY,
    queryOptions,
  );

  const { data, fetchMore, variables, updateQuery, refetch, ...rest } = useQueryResult;

  const cacheManager = useCacheManager<GetAddressesQuery, QueryVariables>(
    GET_ADDRESSES_QUERY,
    useQueryResult,
  );

  // Get address IDs for fetching label assignments
  const addressIds = useMemo(
    () => data?.getAddress?.records?.map((r) => r.addressId) ?? [],
    [data?.getAddress?.records],
  );

  // Fetch label assignments separately to avoid join issues
  const { labelAssignmentsMap } = useEntityAssignments({
    entity: 'Address',
    ids: addressIds,
    skip: !addressIds.length,
  });

  const [addresses, addressesMap] = useMemo(() => {
    if (!data?.getAddress?.records) {
      return [[], null];
    }

    const records = data.getAddress.records.map((address) => {
      const mapped = mapAddress(address);
      // Merge label assignments from separate query
      return {
        ...mapped,
        labelAssignments: labelAssignmentsMap.get(address.addressId) ?? [],
      };
    });

    return [
      records,
      records.reduce((acc: Record<string, Address>, address) => {
        acc[address.addressId] = address;

        return acc;
      }, {}),
    ];
  }, [data?.getAddress.records, labelAssignmentsMap]);

  const { loadMore, hasReachedEnd } = useLoadMore<GetAddressesQuery>({
    offset: data?.getAddress?.records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getAddress.metadata?.totalRecords ?? 0;

  return {
    addresses,
    addressesMap,
    loadMore,
    hasMore: data?.getAddress.metadata?.totalRecords
      ? totalRecords > (data?.getAddress?.records?.length ?? 0)
      : !hasReachedEnd,
    totalRecords,
    cacheManager,
    ...rest,
  };
};

export const useAddress = (addressId?: string) => {
  const { showErrorAlert } = useNotifications();
  const [updateAddressMutation] = useMutation<{
    updateAddress: Partial<Address>[];
  }>(UPDATE_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      // TODO: Move to a generic error handler
      let message = 'Something went wrong updating the site, please try again';
      if (isAddressDuplicateError(error)) {
        message = ADDRESS_DUPLICATE_SITE_MESSAGE;
      }
      showErrorAlert(message);
    },
  });

  const { setAssociatedAddressesPlaceholders } = useConversationViewsStore();

  const updateAddress = useCallback(
    async (
      address: NullableDeepPartial<AddressRaw>,
      conversationId?: string,
      onCompletedCb?: () => void,
    ) => {
      const patch = transformDropdownOptionsToString(
        removeNotDefinedProperties({
          address1: address.address1,
          address2: address.address2,
          primaryClientId: address.primaryClientId ?? address.primaryClient?.clientId,
          primaryProjectId: address.primaryProjectId,
          city: address.city,
          clientActivatedMembership: address.clientActivatedMembership,
          platformSlaId: address.platformSlaId,
          ownerId: address.ownerId || null,
          businessName: address.businessName,
          state: address.state,
          orgNotes: address.orgNotes,
          zip: address.zip,
          brandId: address.brandId,
          clientSelectedMembership: address.clientSelectedMembership,
          tosAccepted: address.tosAccepted,
          membershipSalesNotes: address.membershipSalesNotes,

          // System Infrastructure
          sitePrimaryEquipmentLocation: address.sitePrimaryEquipmentLocation,
          siteNetworkLocation: address.siteNetworkLocation,
          siteWiredNetworkBrand: address.siteWiredNetworkBrand,
          siteWirelessNetworkBrand: address.siteWirelessNetworkBrand,
          site3rdPartyNetworkProvider: address.site3rdPartyNetworkProvider,
          siteInternetService: address.siteInternetService,

          // subSystems
          siteRemoteSystemsManagement: address.siteRemoteSystemsManagement,
          siteRsmUrl: address.siteRsmUrl,
          siteSecondaryRemoteSystemsManagement: address.siteSecondaryRemoteSystemsManagement,
          siteSecondaryRsmUrl: address.siteSecondaryRsmUrl,
          siteTertiaryRemoteSystemsManagement: address.siteTertiaryRemoteSystemsManagement,
          siteTertiaryRsmUrl: address.siteTertiaryRsmUrl,
          rsmNotes: address.rsmNotes,
          isMonitored: address.isMonitored,

          siteTvServiceProvider: address.siteTvServiceProvider,
          siteControlSystemBrand: address.siteControlSystemBrand,
          siteShadeType: address.siteShadeType,
          siteShadeInterface: address.siteShadeInterface,
          siteShadeBrand: address.siteShadeBrand,
          siteLightingSystemType: address.siteLightingSystemType,
          siteLightingSystemBrand: address.siteLightingSystemBrand,
          siteSecuritySystemIntegrated: address.siteSecuritySystemIntegrated,
          siteSecuritySystemBrand: address.siteSecuritySystemBrand,
          siteCameraSystemType: address.siteCameraSystemType,
          siteCameraSystemBrand: address.siteCameraSystemBrand,
          hvacIntegration: address.hvacIntegration,
          siteHvacBrand: address.siteHvacBrand,
          siteStreamingPlatform: address.siteStreamingPlatform,
          siteAccessControlSystemSmartLocks: address.siteAccessControlSystemSmartLocks,
          siteAudioSystemType: address.siteAudioSystemType,
          sitePrimaryAudioInterface: address.sitePrimaryAudioInterface,
          siteVideoSystemType: address.siteVideoSystemType,
          siteVideoDistributionBrand: address.siteVideoDistributionBrand,
          sitePhoneSystemType: address.sitePhoneSystemType,
          sitePhoneSystemBrand: address.sitePhoneSystemBrand,
          siteIntercom: address.siteIntercom,
          siteVideoDoorbell: address.siteVideoDoorbell,
        }),
      );
      if (!address.addressId) {
        return;
      }

      const { data } = await updateAddressMutation({
        variables: {
          conditions: [
            {
              attribute: 'addressId',
              operator: '=',
              value: address.addressId,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateAddress: [
            {
              ...address,
              ...patch,
              ownerId: <string>patch.ownerId || address.ownerId || null,
            } as Address,
          ],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updateAddress?.[0];

          const id = cache.identify({
            __typename: 'Address',
            id: address.addressId,
          });

          cache.modify({
            id,
            fields: {
              ...generateFieldModifiersMap({
                ...updated,
                __typename: 'Address',
              }),
              primaryClient(existingItem = {}, { toReference }) {
                const existingClient = existingItem as Reference;
                if (!address.primaryClient) {
                  return existingClient;
                }

                return toReference(address.primaryClient);
              },
              platformUser(_, { toReference }) {
                return address.ownerId
                  ? toReference({ __typename: 'PlatformUser', id: address.ownerId })
                  : null;
              },
              brand: (_, { toReference }) => {
                return address.brandId
                  ? toReference({ __typename: 'Brand', id: address.brandId })
                  : null;
              },
            },
          });

          if (!conversationId) return;

          const cId = cache.identify({
            __typename: 'Conversation',
            id: conversationId,
          });
          cache.modify({
            id: cId,
            fields: {
              associatedAddresses(_, { toReference }) {
                const newAddress = {
                  ...updated,
                  ...address,
                };
                return [toReference(newAddress) ?? newAddress];
              },
            },
          });
        },
        ...(onCompletedCb ? { onCompleted: onCompletedCb } : {}),
      });

      return data?.updateAddress;
    },
    [updateAddressMutation],
  );

  const [deleteConversationToAddressMutation] = useMutation<{
    deleteConversationToAddress: {
      conversationId: string;
      partnerId?: number;
      addressId?: string;
      createdAt?: string;
    }[];
  }>(DELETE_CONVERSATION_TO_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong disassociating site from conversation, please try again',
      );
    },
  });

  const deleteConversationToAddress = useCallback(
    (conversationId: string, partnerId?: number) => {
      const conditions = [
        {
          attribute: 'conversationId',
          operator: '=',
          value: conversationId,
        },
      ];

      if (addressId) {
        conditions.push({ attribute: 'addressId', operator: '=', value: addressId });
      }

      if (!isNullish(partnerId)) {
        conditions.push({
          attribute: 'partnerId',
          operator: '=',
          value: partnerId.toString(),
        });
      }

      setAssociatedAddressesPlaceholders({
        [conversationId]: [],
      });

      return deleteConversationToAddressMutation({
        variables: {
          conditions,
        },
        update(cache, { data }) {
          const deletedRecords = data?.deleteConversationToAddress;
          if (!deletedRecords) return;

          updateCachedConversation({
            cache,
            props: {
              associatedAddresses: [],
            },
            conversationId,
          });

          if (!addressId) {
            deletedRecords.forEach(({ addressId }) => {
              const id = cache.identify({
                __typename: 'Address',
                id: addressId,
              });

              cache.evict({ id });
            });

            cache.gc();
          }
        },
        optimisticResponse: {
          deleteConversationToAddress: [
            {
              conversationId,
              addressId,
              partnerId,
              createdAt: new Date().toISOString(),
            },
          ],
        },
      });
    },
    [addressId, deleteConversationToAddressMutation, setAssociatedAddressesPlaceholders],
  );

  const [createConversationToAddressMutation] = useMutation<{
    createConversationToAddress: {
      conversationId: string;
      addressId: string;
      partnerId?: number;
    }[];
  }>(CREATE_CONVERSATION_TO_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong adding a new site to conversation, please try again',
      );
    },
  });

  const createConversationToAddress = useCallback(
    async (conversationId: string, address: Address, partnerId?: number | null) => {
      if (conversationId && address) {
        setAssociatedAddressesPlaceholders({
          [conversationId]: [address],
        });
      }

      const { data } = await createConversationToAddressMutation({
        variables: {
          data: {
            conversationId,
            addressId: address.addressId,
            partnerId,
          },
        },
        update(cache) {
          const id = cache.identify({
            __typename: 'Conversation',
            id: conversationId,
          });
          cache.modify({
            id,
            fields: {
              associatedAddresses(_, { toReference }) {
                return [toReference(address) ?? address];
              },
            },
          });
        },
        optimisticResponse: {
          createConversationToAddress: [
            {
              conversationId,
              addressId: address.addressId,
              partnerId: partnerId ?? undefined,
            },
          ],
        },
        refetchQueries: [
          {
            query: GET_CONVERSATION_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'conversationId',
                    value: conversationId,
                    operator: '=',
                  },
                ],
                limit: 1,
              },
            },
          },
        ],
        awaitRefetchQueries: false,
      });

      return data?.createConversationToAddress;
    },
    [createConversationToAddressMutation, setAssociatedAddressesPlaceholders],
  );

  const [associateClientsWithAddressMutation] = useMutation<{
    associateClientsWithAddress: {
      success: boolean;
    };
  }>(ASSOCIATE_CLIENTS_WITH_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong associating clients with a site, please try again');
    },
  });

  const associateClientsWithAddress = useCallback(
    async ({
      clientIds,
      addressId,
      partnerId,
    }: {
      clientIds: string[];
      addressId: string;
      partnerId: number;
    }) => {
      const { data } = await associateClientsWithAddressMutation({
        variables: {
          data: {
            clientIds,
            addressId,
            partnerId,
          },
        },
      });
      return !!data?.associateClientsWithAddress?.success;
    },
    [associateClientsWithAddressMutation],
  );

  const [createClientToAddressMutation] = useMutation<{
    createClientToAddress: ClientToAddress[];
  }>(CREATE_CLIENT_TO_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong adding a new site to requester, please try again');
    },
  });

  const createClientToAddress = useCallback(
    async ({
      client,
      address,
      partnerId,
      filters,
      sort,
      limit,
      offset,
    }: {
      client: DeepPartial<ClientRaw> | null;
      address: Partial<Address>;
      partnerId?: number | null;
      filters?: ConditionInput[];
      sort?: SortingInput;
      limit?: number;
      offset?: number;
    }) => {
      if (!client) {
        return;
      }

      const { data } = await createClientToAddressMutation({
        variables: {
          data: {
            clientId: client.clientId,
            addressId: address.addressId,
            partnerId,
          },
        },
        optimisticResponse: {
          createClientToAddress: [
            {
              clientId: client.clientId ?? '',
              addressId: address.addressId!,
              partnerId: partnerId ?? undefined,
              createdAt: new Date().toISOString(),
            },
          ],
        },
        update: (
          cache,
          {
            data: response,
          }: {
            data?: {
              createClientToAddress?: ClientToAddress[];
            } | null;
          },
        ) => {
          const updated = response?.createClientToAddress?.[0];

          if (!updated) return;

          const normalizedClient = {
            ...client,
            primaryEmail: client.primaryEmail ?? null,
            primaryPhoneNumber: client.primaryPhoneNumber ?? null,
          };

          const id = cache.identify({
            __typename: 'Client',
            id: client.clientId,
          });

          cache.modify({
            id,
            fields: {
              addresses(currentValue = [], { toReference }) {
                const existingAddresses = currentValue as Reference[];
                if (
                  existingAddresses.find((it) => it.__ref === `Address:${address.addressId}`)
                ) {
                  return existingAddresses;
                }

                if (!address.addressId) {
                  return existingAddresses;
                }

                return [
                  ...existingAddresses,
                  toReference({ __typename: 'Address', id: address.addressId }),
                ];
              },
            },
          });

          if (normalizedClient.clientId) {
            emitAppEvent(AppEvents.ClientsToAddressAssociation, {
              clientIds: [normalizedClient.clientId],
              address: {
                ...address,
                primaryClient: normalizedClient,
              } as Address,
            });
          }

          cache.updateQuery<GetAddressesQuery, QueryVariables>(
            {
              query: GET_ADDRESSES_QUERY,
              variables: {
                skipTotalRecords: false,
                params: {
                  filters,
                  sorting: sort ?? [
                    { attribute: 'city', desc: false },
                    { attribute: 'address1', desc: false },
                  ],
                  limit,
                  offset: offset ?? 0,
                },
              },
            },
            (existingData) => {
              if (!existingData?.getAddress?.records) return;

              const records = [...existingData.getAddress.records];

              if (address) {
                records.push({
                  ...address,
                  id: address.addressId!,
                } as AddressRaw);
              }

              return {
                getAddress: {
                  __typename: 'AddressDataResponse',
                  records,
                  metadata: existingData.getAddress.metadata ?? {},
                },
              };
            },
          );
        },
      });

      return data?.createClientToAddress;
    },
    [createClientToAddressMutation],
  );

  const [deleteClientToAddressMutation] = useMutation<{
    deleteClientToAddress: {
      clientId: string;
      addressId?: string;
    }[];
  }>(DELETE_CLIENT_TO_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong disassociating site from requester, please try again',
      );
    },
  });

  const deleteClientToAddress = useCallback(
    (
      clientId: string,
      partnerId?: number,
      entityTypeToRemoveFromCache: EntityNames.Client | EntityNames.Site = EntityNames.Client,
    ) => {
      if (!addressId) {
        return;
      }

      const conditions = [
        {
          attribute: 'clientId',
          operator: '=',
          value: clientId,
        },
        { attribute: 'addressId', operator: '=', value: addressId },
      ];

      if (!isNullish(partnerId)) {
        conditions.push({
          attribute: 'partnerId',
          operator: '=',
          value: partnerId.toString(),
        });
      }

      return deleteClientToAddressMutation({
        variables: {
          conditions,
        },
        update(cache, { data }) {
          if (!data?.deleteClientToAddress?.length) return;
          if (!data.deleteClientToAddress[0].clientId) return;

          if (entityTypeToRemoveFromCache === EntityNames.Client) {
            const id = cache.identify({
              __typename: 'Client',
              id: clientId,
            });
            cache.evict({ id });
          }
          if (entityTypeToRemoveFromCache === EntityNames.Site) {
            const id = cache.identify({
              __typename: 'Address',
              id: addressId,
            });
            cache.evict({ id });
          }

          cache.gc();
        },
        optimisticResponse: {
          deleteClientToAddress: [
            {
              clientId,
              addressId,
            },
          ],
        },
      });
    },
    [addressId, deleteClientToAddressMutation],
  );

  return {
    updateAddress,
    deleteConversationToAddress,
    createConversationToAddress,
    createClientToAddress,
    deleteClientToAddress,
    associateClientsWithAddress,
  };
};

export function useAddressesOptions(
  {
    clientIds,
    filters,
    sort,
    desc = false,
    limit,
  }: {
    clientIds?: string | string[];
    filters?: ConditionInput[];
    sort?: SortingInput;
    desc?: boolean;
    limit?: number;
  },
  options?: QueryHookOptions<GetAddressesQuery>,
) {
  const { addressesMap: addresses } = useAddresses(
    {
      clientIds,
      limit,
      filters,
      sort,
      desc,
    },
    options,
  );

  return useMemo(() => {
    if (!addresses) return [];
    return Object.keys(addresses)
      .map((addressId) => ({
        id: addressId,
        label: addresses[addressId].fullAddress ?? '',
      }))
      .sort(sortAlphabetically('label'));
  }, [addresses]);
}

export const useCreateAddress = (options?: {
  filters?: ConditionInput[];
  limit?: number;
  sort?: SortingInput[] | SortingInput;
  offset?: number;
}) => {
  const { filters, limit, sort, offset = 0 } = options ?? {};
  const { showErrorAlert } = useNotifications();

  const [createAddressMutation, { loading }] = useMutation<{
    createAddress: Address[];
  }>(CREATE_ADDRESS_MUTATION, {
    onError: (error) => {
      console.error(error);
      // TODO: Move to a generic error handler
      let message = 'Something went wrong creating the site, please try again';
      if (isAddressDuplicateError(error)) {
        message = ADDRESS_DUPLICATE_SITE_MESSAGE;
      }

      showErrorAlert(message);
    },
  });

  const createAddress = useCallback(
    async (values: CreateAddressValues) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createAddressMutation({
        variables: {
          data: values,
        },
        optimisticResponse: {
          createAddress: [
            {
              id: tempId,
              addressId: tempId,
              ...values,
            } as Address,
          ],
        },
        update(cache, { data }) {
          const createdAddress = data?.createAddress?.[0];
          if (!createdAddress?.addressId) return;

          cache.updateQuery<GetAddressesQuery, QueryVariables>(
            {
              query: GET_ADDRESSES_QUERY,
              variables: {
                skipTotalRecords: false,
                params: {
                  filters,
                  limit,
                  sorting: sort ?? [
                    { attribute: 'city', desc: false },
                    { attribute: 'address1', desc: false },
                  ],
                  offset,
                },
              },
            },
            (existingData) => {
              if (!existingData?.getAddress?.records) return;

              const records = [...existingData.getAddress.records];

              if (createdAddress) {
                records.push(createdAddress as AddressRaw);
              }

              return {
                getAddress: {
                  __typename: 'AddressDataResponse',
                  records,
                  metadata: existingData.getAddress.metadata ?? {},
                },
              };
            },
          );
        },
      });

      const createdAddresses = data?.createAddress.map(
        (address) => mapAddress(address as AddressRaw) as Address,
      );

      return createdAddresses?.[0];
    },
    [createAddressMutation, filters, limit, offset, sort],
  );

  return { createAddress, isLoading: loading };
};

function transformDropdownOptionsToString(
  properties: Record<string, string | boolean | { label: string; id: string } | null>,
) {
  for (const key in properties) {
    if (
      Object.hasOwn(properties, key) &&
      typeof properties[key] !== 'string' &&
      typeof properties[key] !== 'boolean' &&
      !isNullish(properties[key]) &&
      'id' in properties[key]
    ) {
      properties[key] = properties[key].id;
    }
  }

  return properties;
}

export const useAddressRecord = ({ addressId }: { addressId: string }) => {
  const { addressesMap: addresses, loading } =
    useAddresses(
      {
        filters: [
          {
            attribute: 'addressId',
            operator: '=',
            value: addressId,
          },
        ],
        includeDeleted: true,
      },
      {
        skip: !addressId,
      },
    ) ?? {};

  const selectedAddress = useMemo(
    () => (addressId ? addresses?.[addressId] : undefined),
    [addressId, addresses],
  );

  return useMemo(
    () => ({ address: selectedAddress, isLoading: loading }),
    [loading, selectedAddress],
  );
};

export const useResolvedAddress = (address?: Address | null) => {
  const addressId = address?.addressId ?? '';
  const needsHydration = Boolean(addressId && address?.partner === undefined);
  const { address: fetchedAddress, isLoading } = useAddressRecord({
    addressId: needsHydration ? addressId : '',
  });

  const resolvedAddress = useMemo(() => {
    if (needsHydration) {
      return fetchedAddress ?? address ?? undefined;
    }
    return address ?? fetchedAddress ?? undefined;
  }, [address, fetchedAddress, needsHydration]);

  const loading = Boolean(needsHydration && isLoading && !fetchedAddress);

  return {
    resolvedAddress,
    loading,
    isHydrated: needsHydration ? Boolean(fetchedAddress) : Boolean(resolvedAddress),
  };
};

export async function getAddressesByClient({
  apolloClient,
  entityId,
}: {
  apolloClient: ApolloClient<object>;
  entityId: string;
}) {
  if (!entityId) {
    return [];
  }

  const {
    data: {
      getAddress: { records },
    },
  } = await apolloClient.query<GetAddressesQuery, QueryVariables>({
    query: GET_ADDRESSES_QUERY,
    variables: {
      skipTotalRecords: false,
      params: {
        filters: [{ attribute: 'clients/clientId', operator: '=', value: entityId }],
      },
    },
  });

  return records;
}

export interface MergeAddressMessageParams {
  addressOriginId: string;
  addressTargetId: string;
}

export const useMergeAddress = () => {
  const [mergeAddressMutation, { loading }] = useMutation<{
    mergeAddress: {
      error: string | null;
      success: boolean;
    };
  }>(MERGE_ADDRESS_MUTATION, {
    onError: (error) => {
      console.warn(error);
    },
    update(
      cache,
      { data },
      { variables }: { variables?: { params?: MergeAddressMessageParams } },
    ) {
      const addressOriginId = variables?.params?.addressOriginId;
      if (data?.mergeAddress.success && addressOriginId) {
        const id = cache.identify({
          __typename: 'Address',
          id: addressOriginId,
        });

        cache.evict({ id });
        cache.gc();
      }
    },
  });

  const mergeAddress = useCallback(
    async (values: MergeAddressMessageParams) => {
      const response = await mergeAddressMutation({
        variables: {
          params: values,
        },
      });
      return response;
    },
    [mergeAddressMutation],
  );

  return { mergeAddress, loading };
};
